import {
  LegacyCard,
  InlineGrid,
  EmptyState,
  InlineStack,
  Button,
  BlockStack,
  Text,
  Tag,
  Box,
} from "@shopify/polaris";
import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { API_URL } from "constants.js";
import { useParams, useNavigate } from "react-router-dom";
import useRestorableState from "hooks/useRestorableState";
import {
  fetchCategoryFilesDetails,
  setDestroy,
} from "containers/document/category-files/catFiles.Slice";
import { RootState, AppDispatch } from "configureStore";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import HeaderLine from "components/routePath/HeaderLine";
import UploadFile from "features/upload-file/upload-file";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxStyle,
} from "components/modalx/modalx.slice";
import DataTable from "components/datatable/Datatable";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import Loading from "components/loading/Loading";
import { EditIcon, ViewIcon, CircleDownIcon } from "@shopify/polaris-icons";
import { Customer } from "types/Customer";
import PaginationControls from "components/pagination/PaginationControls";
import { formatFileNumberWithSubNumber } from "utils/formatFileNumberDisplay";
import DatePicker from "features/datepicker/date-picker";
import DocumentFiltersPopover, {
  type DocumentFiltersValues,
} from "containers/document/category-files/DocumentFiltersPopover";
import { fetchById as fetchUserById } from "services/user-select.service";
import { fetchById as fetchCustomerById } from "services/customers.service";
import datePickerValueToString from "containers/document/category-files/datePickerValueToString";
import { AuditLogPopover } from "components/audit-log/audit-log-popover";
import AddToCartButton from "features/document-cart/AddToCartButton";
import { addFileThunk } from "features/document-cart/documentCart.slice";
import { toast } from "sonner";
import { fetchCategoryDetails } from "containers/document/category-tree/category-tree.Slice";
import AttachToDocumentModal, {
  AttachToDocumentResult,
} from "components/attach-to-document/AttachToDocumentModal";
import AnnulledBadge from "components/document-annulment/AnnulledBadge";

interface DocumentDataTableProps {
  id: string;
  source?: string;
}

const mapFilterSignedToIsSigned = (filterSigned: string): string | undefined => {
  if (filterSigned === "signed") return "true";
  if (filterSigned === "unsigned") return "false";
  return undefined;
};

const CategoryFileContainer: React.FC<DocumentDataTableProps> = ({
  id,
  source = "profile",
}) => {
  const { t } = useTranslation();
  const history = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const [selectedFileIds, setSelectedFileIds] = useState<Record<string, true>>(
    {}
  );
  const selectedCount = Object.keys(selectedFileIds).length;
  // Stable across re-renders while the selection itself doesn't change —
  // AttachToDocumentModal resets its search on every new `fileIds` array.
  const selectedFileIdsList = useMemo(
    () => Object.keys(selectedFileIds),
    [selectedFileIds]
  );
  const [isAttachModalOpen, setIsAttachModalOpen] = useState(false);
  const { companyId, userId } = useParams<{
    companyId: string;
    userId: string;
  }>();
  const isOperational = source === "document" ? true : false;
  // In the cabinet the customer is almost always empty, so a phone card shows
  // the document date there instead of the customer and the responsible user.
  const isProfile = source === "profile";
  // Filters and pagination are remembered per category, so the browser's Back
  // button brings the list back the way it was left.
  const [paginationState, setPaginationState] = useRestorableState<{
    page: number;
    pageSize: number;
  }>(`documents-pagination:${id}`, {
    page: 1,
    pageSize: 25,
  });
  const { page, pageSize } = paginationState;
  const [dateFrom, setDateFrom] = useRestorableState<string>(
    `documents-date-from:${id}`,
    ""
  );
  const [dateTo, setDateTo] = useRestorableState<string>(
    `documents-date-to:${id}`,
    ""
  );
  const [filterUserId, setFilterUserId] = useRestorableState<string>(
    `documents-user:${id}`,
    ""
  );
  const [filterCustomerId, setFilterCustomerId] = useRestorableState<string>(
    `documents-customer:${id}`,
    ""
  );
  const [filterSigned, setFilterSigned] = useRestorableState<string>(
    `documents-signed:${id}`,
    ""
  );
  const [filterPopoverActive, setFilterPopoverActive] = useState(false);
  const [filterUserLabel, setFilterUserLabel] = useState<string>("");
  const [filterCustomerLabel, setFilterCustomerLabel] = useState<string>("");
  const pageSizeOptions = [10, 25, 50, 100];
  const previousCategoryIdRef = useRef<string | null>(null);
  const pendingRequestRef = useRef<boolean>(false);
  const lastFetchParamsRef = useRef<{
    companyId: string;
    catId: string;
    source: string;
    page: number;
    pageSize: number;
    dateFrom?: string;
    dateTo?: string;
    userId?: string;
    customerId?: string;
    isSigned?: string;
  } | null>(null);

  const { catFilesData, categoryType, isLoading, isError } = useSelector(
    (state: RootState) => state.documentFilesReducer
  );
  const pagination = catFilesData?.pagination;

  useEffect(() => {
    setSelectedFileIds({});
  }, [id]);

  // Track which category we currently hold settled data for, so we can tell
  // an initial/category-switch load (→ skeleton) apart from an in-place
  // refetch such as pagination or filter changes (→ keep rows, dim lightly).
  const [loadedCategoryId, setLoadedCategoryId] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (!isLoading && catFilesData) {
      setLoadedCategoryId(id);
    }
  }, [isLoading, catFilesData, id]);

  // Resolve user display name for applied filter tag
  useEffect(() => {
    if (!companyId || !filterUserId) {
      setFilterUserLabel("");
      return;
    }
    let cancelled = false;
    fetchUserById(filterUserId, companyId)
      .then((data: { userEntity?: { name?: string; email?: string } }) => {
        if (cancelled) return;
        const u = data?.userEntity;
        setFilterUserLabel(u?.name || u?.email || filterUserId);
      })
      .catch(() => {
        if (!cancelled) setFilterUserLabel(filterUserId);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, filterUserId]);

  // Resolve customer display name for applied filter tag
  useEffect(() => {
    if (!companyId || !filterCustomerId) {
      setFilterCustomerLabel("");
      return;
    }
    let cancelled = false;
    fetchCustomerById(companyId, filterCustomerId)
      .then((customer: { name?: string }) => {
        if (cancelled) return;
        setFilterCustomerLabel(customer?.name || filterCustomerId);
      })
      .catch(() => {
        if (!cancelled) setFilterCustomerLabel(filterCustomerId);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, filterCustomerId]);

  useEffect(() => {
    if (pagination) {
      if (isLoading || pendingRequestRef.current) {
        return;
      }
      const serverPage =
        Number.isFinite(pagination.page) && pagination.page > 0
          ? pagination.page
          : null;
      const serverPageSize =
        Number.isFinite(pagination.pageSize) && pagination.pageSize > 0
          ? pagination.pageSize
          : null;

      let nextPageValue: number | undefined;
      let nextPageSizeValue: number | undefined;

      setPaginationState((previous) => {
        let nextState = previous;

        nextPageValue = previous.page;
        nextPageSizeValue = previous.pageSize;

        if (serverPage && serverPage !== previous.page) {
          nextState =
            nextState === previous
              ? { ...previous, page: serverPage }
              : { ...nextState, page: serverPage };
          nextPageValue = serverPage;
        } else {
          nextPageValue =
            nextState === previous ? previous.page : nextState.page;
        }

        const currentPageSize =
          nextState === previous ? previous.pageSize : nextState.pageSize;
        nextPageSizeValue = currentPageSize;

        if (serverPageSize && serverPageSize !== currentPageSize) {
          nextState =
            nextState === previous
              ? { ...previous, pageSize: serverPageSize }
              : { ...nextState, pageSize: serverPageSize };
          nextPageSizeValue =
            nextState === previous ? previous.pageSize : nextState.pageSize;
        }

        return nextState;
      });

      if (lastFetchParamsRef.current) {
        lastFetchParamsRef.current = {
          ...lastFetchParamsRef.current,
          page:
            (Number.isFinite(nextPageValue) && nextPageValue! > 0
              ? nextPageValue
              : lastFetchParamsRef.current.page) ??
            lastFetchParamsRef.current.page,
          pageSize:
            (Number.isFinite(nextPageSizeValue) && nextPageSizeValue! > 0
              ? nextPageSizeValue
              : lastFetchParamsRef.current.pageSize) ??
            lastFetchParamsRef.current.pageSize,
        };
      }
    }
  }, [pagination, setPaginationState]);

  useEffect(() => {
    if (!id || !companyId) {
      return;
    }

    if (isLoading || pendingRequestRef.current) {
      return;
    }

    const hasCategoryChanged =
      previousCategoryIdRef.current !== null &&
      previousCategoryIdRef.current !== id;
    const norm = (v: string | undefined) => (v == null || v === "" ? "" : v);
    const dateFilterChanged =
      lastFetchParamsRef.current &&
      (norm(lastFetchParamsRef.current.dateFrom) !== norm(dateFrom) ||
        norm(lastFetchParamsRef.current.dateTo) !== norm(dateTo));
    const isSignedFilter = mapFilterSignedToIsSigned(filterSigned);
    const otherFiltersChanged =
      lastFetchParamsRef.current &&
      (norm(lastFetchParamsRef.current.userId) !== norm(filterUserId) ||
        norm(lastFetchParamsRef.current.customerId) !== norm(filterCustomerId) ||
        (lastFetchParamsRef.current.isSigned ?? "") !== (isSignedFilter ?? ""));
    const targetPage = hasCategoryChanged || dateFilterChanged || otherFiltersChanged ? 1 : page;

    if (page !== targetPage) {
      setPaginationState((previous) => ({
        ...previous,
        page: targetPage,
      }));
      if (hasCategoryChanged) previousCategoryIdRef.current = id;
      return;
    }

    previousCategoryIdRef.current = id;
    const requestParams = {
      companyId,
      catId: id,
      source,
      page: targetPage,
      pageSize,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      userId: filterUserId || undefined,
      customerId: filterCustomerId || undefined,
      isSigned: isSignedFilter,
    };

    if (
      lastFetchParamsRef.current &&
      lastFetchParamsRef.current.companyId === requestParams.companyId &&
      lastFetchParamsRef.current.catId === requestParams.catId &&
      lastFetchParamsRef.current.source === requestParams.source &&
      lastFetchParamsRef.current.page === requestParams.page &&
      lastFetchParamsRef.current.pageSize === requestParams.pageSize &&
      lastFetchParamsRef.current.dateFrom === requestParams.dateFrom &&
      lastFetchParamsRef.current.dateTo === requestParams.dateTo &&
      lastFetchParamsRef.current.userId === requestParams.userId &&
      lastFetchParamsRef.current.customerId === requestParams.customerId &&
      lastFetchParamsRef.current.isSigned === requestParams.isSigned
    ) {
      return;
    }

    pendingRequestRef.current = true;
    lastFetchParamsRef.current = requestParams;

    const fetchAction = dispatch(fetchCategoryFilesDetails(requestParams));

    if (
      fetchAction &&
      typeof (fetchAction as Promise<unknown>).finally === "function"
    ) {
      (fetchAction as Promise<unknown>).finally(() => {
        pendingRequestRef.current = false;
      });
    } else {
      pendingRequestRef.current = false;
    }
  }, [companyId, id, page, pageSize, source, dateFrom, dateTo, filterUserId, filterCustomerId, filterSigned, isLoading, dispatch, setPaginationState]);

  useEffect(() => {
    return () => {
      dispatch(setDestroy());
    };
  }, [dispatch]);

  const handleEditClick = (fileId: string) => {
    dispatch(setModalxComponent("FileForm"));
    dispatch(
      setModalxComponentProps({
        fileId,
        companyId,
        isCustomerSelectorVisible: true,
        isUserSelectorVisible: true,
        categoryType,
      })
    );
  };

  const handlePdfView = async (id: string) => {
    dispatch(setModalxComponent("PdfView"));
    dispatch(setModalxStyle("large"));
    dispatch(
      setModalxComponentProps({
        id,
        companyId,
      })
    );
  };

  const handleRowClick = (
    companyId: string | undefined,
    cateId: string,
    fileId: string
  ) => {
    const url =
      source === "profile"
        ? `/app/${companyId}/profile/files/${cateId}/${fileId}`
        : `/app/${companyId}/documents/${cateId}/${fileId}`;
    history(url);
  };

  // Selection survives paging, so names are remembered when a row is ticked
  // (the current page alone can't name files selected on other pages).
  const selectedFileNamesRef = useRef<Record<string, string>>({});
  const selectedFileLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    Object.keys(selectedFileIds).forEach((fileId) => {
      const onPage = (catFilesData?.files || []).find(
        (file: any) => file.id === fileId
      );
      const label = onPage
        ? onPage.name || onPage.title || ""
        : selectedFileNamesRef.current[fileId];
      if (label) labels[fileId] = label;
    });
    return labels;
  }, [catFilesData, selectedFileIds]);

  const collection =
    catFilesData?.files?.map((file: any) => ({
      id: file.id,
      selected: !!selectedFileIds[file.id],
      name: (
        <span className="inline-flex items-center gap-2">
          <span>
            {file.name} [
            {file.relatedFiles.length > 0 ? file.relatedFiles.length : "x"}]
          </span>
          {file.annulledAt && <AnnulledBadge />}
        </span>
      ),
      title: file.title,
      customerName:
        file.customer && Array.isArray(file.customer)
          ? file.customer
              .map((customer: Customer) =>
                customer.name
                  ? `${customer.name}${
                      customer.externalCode ? ` [${customer.externalCode}]` : ""
                    }`
                  : ""
              )
              .filter(Boolean) // Remove any empty strings for customers without a name
              .join(", ")
          : "-",
      assignedUser: file.assignedUser?.name ?? "-",
      seriesTitle: `${file.series?.title || ""}${formatFileNumberWithSubNumber(
        file.number,
        file.subNumber
      )}`,
      number: formatFileNumberWithSubNumber(file.number, file.subNumber),
      date: file.date,
      dateTo: file.dateTo,
      downloadUrl: `${API_URL}/${companyId}/files/download/${file.id}`,
      isVisible: file.fileId ? true : false,
    })) || [];

  const handleAddSelectedToCart = async () => {
    if (!companyId || !selectedCount) return;
    const ids = Object.keys(selectedFileIds);
    let added = 0;
    let failed = 0;
    for (const fileId of ids) {
      try {
        const result = await dispatch(
          addFileThunk({
            companyId,
            fileId,
            source: source === "profile" ? "profile" : "document",
          })
        ).unwrap();
        if (!result.alreadyInCart) added += 1;
      } catch {
        failed += 1;
      }
    }
    if (failed < ids.length) {
      setSelectedFileIds({});
    }
    if (added > 0) {
      toast.success(t("document_cart_added"));
    }
    if (failed > 0) {
      toast.error(t("document_cart_add_failed"));
    } else if (added === 0) {
      toast.message(t("document_cart_already_in"));
    }
  };

  const handleAttachToDocumentDone = (result: AttachToDocumentResult) => {
    // Keep the files that did not move selected, so they can be retried.
    setSelectedFileIds(
      Object.fromEntries(result.failed.map((f) => [f.id, true as const]))
    );
    if (companyId && id) {
      // Same refresh pair file-form.slice.js's submit() uses after a parent
      // change: the current page's rows and the tree's per-category counts.
      dispatch(
        fetchCategoryFilesDetails({
          companyId,
          catId: id,
          source,
          page,
          pageSize,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          userId: filterUserId || undefined,
          customerId: filterCustomerId || undefined,
          isSigned: mapFilterSignedToIsSigned(filterSigned),
        })
      );
      dispatch(fetchCategoryDetails({ companyId, source }));
    }
    if (result.ok.length > 0) {
      toast.success(t("attach_to_document_success", { count: result.ok.length }));
    }
    if (result.failed.length > 0) {
      toast.error(t("attach_to_document_failed", { count: result.failed.length }));
    }
  };

  const handleOnFileChange = () => {};

  const handlePageChange = (nextPage: number) => {
    if (nextPage > 0 && nextPage !== page) {
      setPaginationState((previous) => ({
        ...previous,
        page: nextPage,
      }));
    }
  };

  const handlePageSizeChange = (nextPageSize: number) => {
    if (nextPageSize > 0 && nextPageSize !== pageSize) {
      setPaginationState((previous) => ({
        ...previous,
        page: 1,
        pageSize: nextPageSize,
      }));
    }
  };

  const totalItems =
    typeof pagination?.total === "number"
      ? pagination.total
      : collection.length;
  const totalPages =
    typeof pagination?.totalPages === "number"
      ? pagination.totalPages
      : totalItems > 0
      ? Math.ceil(totalItems / pageSize)
      : 0;
  const hasNextPage =
    typeof pagination?.hasNextPage === "boolean"
      ? pagination.hasNextPage
      : totalPages > 0 && page < totalPages;
  const hasPreviousPage =
    typeof pagination?.hasPreviousPage === "boolean"
      ? pagination.hasPreviousPage
      : page > 1;
  const shouldShowPagination = Boolean(pagination);

  const hasFiles = (catFilesData?.files?.length ?? 0) > 0;
  // Fresh, settled data for the category currently in view.
  const showingFreshData = loadedCategoryId === id && !isLoading;
  // Refetching the same category that already has rows (pagination/filters).
  const isRefetchingSameCategory =
    isLoading && loadedCategoryId === id && hasFiles;
  // Nothing valid to show for the current category yet → full skeleton.
  const showSkeleton =
    !isError && !showingFreshData && !isRefetchingSameCategory;

  const uploadNode = (
    <UploadFile
      onChange={handleOnFileChange}
      categoryId={id}
      isCustomerSelectorVisible={true}
      isUserSelectorVisible={true}
      categoryType={categoryType ?? undefined}
      source={source}
    />
  );

  return id ? (
    <div className="relative min-h-[200px]">
      <div className="mb-4">
        {/* On a phone the buttons go under the breadcrumbs instead of squeezing them. */}
        <InlineGrid
          columns={isOperational ? { xs: "1fr", md: "1fr auto" } : "1fr"}
        >
          <HeaderLine catId={id} />
          {isOperational && (
            <InlineStack gap="200" blockAlign="center">
              <DocumentFiltersPopover
                open={filterPopoverActive}
                onOpenChange={setFilterPopoverActive}
                appliedFilters={{
                  dateFrom,
                  dateTo,
                  filterUserId,
                  filterCustomerId,
                  filterSigned,
                }}
                onApplyFilters={(filters: DocumentFiltersValues) => {
                  setDateFrom(filters.dateFrom);
                  setDateTo(filters.dateTo);
                  setFilterUserId(filters.filterUserId);
                  setFilterCustomerId(filters.filterCustomerId);
                  setFilterSigned(filters.filterSigned);
                }}
                hasActiveFilters={
                  [dateFrom, dateTo, filterUserId, filterCustomerId, filterSigned]
                    .filter(Boolean).length > 0
                }
              />
              {uploadNode}
              <AuditLogPopover
                companyId={companyId!}
                subjectType="FileCategory"
                subjectId={id}
                label={""} // Clear label to just show the icon/default
              />
            </InlineStack>
          )}
        </InlineGrid>
      </div>
      {isOperational &&
        [dateFrom, dateTo, filterUserId, filterCustomerId, filterSigned].filter(
          Boolean
        ).length > 0 && (
          <Box paddingBlockEnd="300">
            <InlineStack gap="200" wrap blockAlign="center">
              <Text variant="bodySm" as="span" fontWeight="medium">
                {t("applied_filters") !== "applied_filters"
                  ? t("applied_filters")
                  : "Applied"}
              </Text>
              {dateFrom && (
                <Tag onRemove={() => setDateFrom("")}>
                  {t("date_from")}: {dateFrom}
                </Tag>
              )}
              {dateTo && (
                <Tag onRemove={() => setDateTo("")}>
                  {t("date_to")}: {dateTo}
                </Tag>
              )}
              {filterUserId && (
                <Tag onRemove={() => setFilterUserId("")}>
                  {t("user")}: {filterUserLabel || "…"}
                </Tag>
              )}
              {filterCustomerId && (
                <Tag onRemove={() => setFilterCustomerId("")}>
                  {t("customer")}: {filterCustomerLabel || "…"}
                </Tag>
              )}
              {filterSigned && (
                <Tag onRemove={() => setFilterSigned("")}>
                  {t("status")}:{" "}
                  {filterSigned === "signed"
                    ? t("signed")
                    : t("not_signed")}
                </Tag>
              )}
              <Button
                variant="plain"
                tone="critical"
                size="slim"
                onClick={() => {
                  setDateFrom("");
                  setDateTo("");
                  setFilterUserId("");
                  setFilterCustomerId("");
                  setFilterSigned("");
                }}
              >
                {t("clear_all_filters")}
              </Button>
            </InlineStack>
          </Box>
        )}
      {!isOperational && (
        <Box paddingBlockEnd="400">
          <BlockStack gap="300">
            <InlineStack gap="400" wrap blockAlign="end">
              <div className="min-w-[200px] flex-1">
                <DatePicker
                  title={t("date_from")}
                  selected={dateFrom}
                  onChange={(value) => setDateFrom(datePickerValueToString(value))}
                  isRequired={false}
                  disableDatesAfter=""
                />
              </div>
              <div className="min-w-[200px] flex-1">
                <DatePicker
                  title={t("date_to")}
                  selected={dateTo}
                  onChange={(value) => setDateTo(datePickerValueToString(value))}
                  isRequired={false}
                  disableDatesAfter=""
                />
              </div>
            </InlineStack>
            {(dateFrom || dateTo) && (
              <InlineStack gap="200" wrap blockAlign="center">
                {dateFrom && (
                  <Tag onRemove={() => setDateFrom("")}>
                    {t("date_from")}: {dateFrom}
                  </Tag>
                )}
                {dateTo && (
                  <Tag onRemove={() => setDateTo("")}>
                    {t("date_to")}: {dateTo}
                  </Tag>
                )}
                <Button
                  variant="plain"
                  onClick={() => {
                    setDateFrom("");
                    setDateTo("");
                  }}
                >
                  {t("clear_all_filters")}
                </Button>
              </InlineStack>
            )}
          </BlockStack>
        </Box>
      )}
      {showSkeleton ? (
        <DataTableSkeleton rows={10} columns={11} iconColumnIndexes={[8, 9, 10]} />
      ) : isError ? (
        <EmptyState
          heading={t("not_fetch", { defaultValue: "Could not load files" })}
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        >
          <Button onClick={() => history(0)}>
            {t("retry", { defaultValue: "Retry" })}
          </Button>
        </EmptyState>
      ) : !hasFiles ? (
        <EmptyState
          heading={t("no_files")}
          image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
        >
          {isOperational && uploadNode}
        </EmptyState>
      ) : (
        <div className="relative flex flex-col h-full">
          {isRefetchingSameCategory && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 rounded pointer-events-none">
              <Loading size="big" />
            </div>
          )}
          <LegacyCard>
            {selectedCount > 0 && (
              <Box padding="400" borderBlockEndWidth="025" borderColor="border">
                <InlineStack align="space-between" blockAlign="center">
                  <Text as="span" variant="bodyMd">
                    {t("document_cart_selected_count", { count: selectedCount })}
                  </Text>
                  <InlineStack gap="200">
                    <Button onClick={() => setSelectedFileIds({})}>
                      {t("document_cart_clear_selection")}
                    </Button>
                    {isOperational && (
                      <Button onClick={() => setIsAttachModalOpen(true)}>
                        {t("attach_to_document")}
                      </Button>
                    )}
                    <Button variant="primary" onClick={handleAddSelectedToCart}>
                      {t("document_cart_add_selected")}
                    </Button>
                  </InlineStack>
                </InlineStack>
              </Box>
            )}
            <DataTable
              collection={collection}
              columns={[
                {
                  key: "selected",
                  name: "",
                  type: "checkbox",
                  onClick: (item: any) => {
                    const file = (catFilesData?.files || []).find(
                      (f: any) => f.id === item.id
                    );
                    if (file) {
                      selectedFileNamesRef.current[item.id] =
                        file.name || file.title || "";
                    }
                    setSelectedFileIds((prev) => {
                      const next = { ...prev };
                      if (next[item.id]) delete next[item.id];
                      else next[item.id] = true;
                      return next;
                    });
                  },
                },
                {
                  key: "name",
                  name: t("title"),
                  onClick: (item: any) =>
                    handleRowClick(companyId, id, item.id),
                  type: "clickableColumn",
                  mobileView: "topLeft",
                },
                {
                  key: "addToCart",
                  name: t("document_cart"),
                  type: "custom",
                  mobileView: "topRight",
                  render: (item: any) => (
                    <AddToCartButton
                      fileId={item.id}
                      variant="chip"
                      source={source === "profile" ? "profile" : "document"}
                    />
                  ),
                },
                {
                  key: "customerName",
                  name: t("customer"),
                  onClick: (item: any) =>
                    handleRowClick(companyId, id, item.id),
                  type: "clickableColumn",
                  mobileView: isProfile ? undefined : "bottomLeft",
                },
                {
                  key: "assignedUser",
                  name: t("assignedUser"),
                  onClick: (item: any) =>
                    handleRowClick(companyId, id, item.id),
                  type: "clickableColumn",
                  mobileView: isProfile ? undefined : "bottomRight",
                },
                {
                  key: "seriesTitle",
                  name: t("series"),
                  onClick: (item: any) =>
                    handleRowClick(companyId, id, item.id),
                  type: "clickableColumn",
                },
                {
                  key: "date",
                  name: t("document_date"),
                  onClick: (item: any) =>
                    handleRowClick(companyId, id, item.id),
                  type: "clickabledate",
                  isShowTime: false,
                  mobileView: isProfile ? "bottomLeft" : undefined,
                },
                {
                  key: "dateTo",
                  name: t("dateTo"),
                  onClick: (item: any) =>
                    handleRowClick(companyId, id, item.id),
                  type: "clickabledate",
                  isShowTime: false,
                },
                {
                  key: "Edit",
                  name: t("edit"),
                  onClick: (item: any) => handleEditClick(item.id),
                  type: "actionBtn",
                  Icon: EditIcon,
                  isDisabled: !isOperational,
                },
                {
                  key: "view",
                  name: t("view"),
                  onClick: (item: any) => handlePdfView(item.id),
                  type: "specificAction",
                  Icon: ViewIcon,
                },
                {
                  key: "downloadUrl",
                  name: t("download"),
                  onClick: (item: any) =>
                    window.open(item.downloadUrl, "_blank"),
                  url: "downloadUrl",
                  type: "downloadurl",
                  Icon: CircleDownIcon,
                },
              ]}
              maxBodyHeight="100%"
            />
            {shouldShowPagination && (
              <div className="border-t border-[rgba(0,0,0,0.06)] p-4">
                <PaginationControls
                  page={page}
                  pageSize={pageSize}
                  total={totalItems}
                  totalPages={totalPages}
                  hasNextPage={hasNextPage}
                  hasPreviousPage={hasPreviousPage}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  pageSizeOptions={pageSizeOptions}
                  isLoading={isLoading}
                />
              </div>
            )}
          </LegacyCard>
        </div>
      )}
      {companyId && (
        <AttachToDocumentModal
          companyId={companyId}
          fileIds={selectedFileIdsList}
          fileLabels={selectedFileLabels}
          open={isAttachModalOpen}
          onClose={() => setIsAttachModalOpen(false)}
          onDone={handleAttachToDocumentDone}
        />
      )}
    </div>
  ) : (
    <></>
  );
};

export default CategoryFileContainer;
